import React, { useMemo, memo } from 'react';
import { makeStyles } from '~/UIKit/Style';
import { List, Theme, useMediaQuery } from '@mui/material';
import { VERTICAL_SEPARATOR } from '../../../Constants/Colors.constants';
import { IDragHistoryItem, useDragAndDropContainer } from '../../../Hooks';
import { IFitoListItem } from '../models';
import { FitoListItem } from './FitoListItem';
import { DataTable } from '~/Components/DataTable';
import { TableDefinition } from '~/Components/DataTable/DataTable.types';

interface IFitoListContentProps {
  items: IFitoListItem[];
  listName?: string;
  isBeingRearranged?: boolean;
  onItemRearranged?: (items: IFitoListItem[]) => void;
  addToDnDHistory?: (history: IDragHistoryItem) => void;
  onSelect?: (id: string, checked?: boolean) => void;
  multiSelect?: boolean;
  isSelectPossible?: boolean;
  isSelectionActive?: boolean;
  desktopView?: {
    items: any[];
    tableDefinition: TableDefinition;
    onRowClicked?: (row: any) => void;
    onRowSelected?: (row: any) => void;
  };
}

const useStyles = makeStyles({
  listContainer: {
    width: '100%',
    '&>.MuiListItem-container': {
      border: `thin solid ${VERTICAL_SEPARATOR}`,
      borderLeft: 0,
      borderRight: 0,
      borderBottom: 0,
      '&:last-of-type': {
        borderBottom: `thin solid ${VERTICAL_SEPARATOR}`,
      },
    },
  },
});

export const FitoListContent = memo(
  ({
    items,
    listName = '',
    isBeingRearranged,
    onItemRearranged,
    onSelect,
    addToDnDHistory,
    multiSelect = false,
    isSelectPossible = true,
    isSelectionActive = false,
    desktopView,
  }: IFitoListContentProps) => {
    const { classes } = useStyles();

    const { moveItem, findItemIndex } = useDragAndDropContainer(
      items,
      onItemRearranged || (() => {}),
      addToDnDHistory || (() => {}),
    );

    const isAnyItemSelected = useMemo(() => !!items.filter((item) => item.checked).length, [items]);
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

    if (isMobile || !desktopView) {
      return (
        <List className={classes.listContainer} disablePadding data-testid="FitoList">
          {items.map((item: IFitoListItem, index: number) => (
            <FitoListItem
              isBeingRearranged={isBeingRearranged}
              isAnyItemSelected={isAnyItemSelected}
              item={item}
              index={index}
              moveItem={moveItem}
              findItemIndex={findItemIndex}
              onSelect={onSelect}
              listName={listName}
              key={`${listName}_${index}`}
              data-testid={`item_${index}`}
              multiSelect={multiSelect}
              isSelectPossible={isSelectPossible}
              isSelectionActive={isSelectionActive}
            />
          ))}
        </List>
      );
    }
    return (
      <DataTable
        data={desktopView.items}
        tableDefinition={desktopView.tableDefinition}
        onRowClicked={desktopView?.onRowClicked}
        onRowSelected={desktopView?.onRowSelected}
      />
    );
  },
);
